import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import PlastisolOptionsPanel from '../components/PlastisolOptionsPanel';
import PlastisolDrawingBoard from '../components/PlastisolDrawingBoard';
import Summary from '../components/Summary';
import ConfiguratorShell from '../components/ConfiguratorShell';
import { useTranslation } from '../context/TranslationContext';
import {
  plastisolProfileSystems,
  glassTextures,
  glazingTypes,
  colors,
  woodJointOptions,
  PRICING,
  normalizePlastisolProfileSystem,
} from '../data/mockData';
import { PLATISOL_DEFAULT_EXTERIOR_COLOR } from '../components/PlastisolOptionsPanel';
import { useConfiguratorDirtyState } from '../hooks/useConfiguratorDirtyState';
import { useConfiguratorLoadState } from '../hooks/useConfiguratorLoadState';
import {
  createDefaultAdditionalItems,
  withDefaultAdditionalItems,
  applyAdditionalItemsChange,
} from '../lib/additionalItems';

const DEFAULT_PLATISOL_CONFIG = {
  meters: 1,
  width: 1000,
  height: 1000,
  profileSystem: 'plastisol-zetwerk',
  productType: 'plastisol',
  exteriorColor: PLATISOL_DEFAULT_EXTERIOR_COLOR, // RAL 9010
  quantity: 1,
};

const PlastisolZetwerk = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const drawingBoardRef = useRef(null);
  const saveCurrentRef = useRef(null);

  const buildDefaultConfig = () =>
    withDefaultAdditionalItems({
      ...DEFAULT_PLATISOL_CONFIG,
      profileSystem: normalizePlastisolProfileSystem(DEFAULT_PLATISOL_CONFIG.profileSystem),
      exteriorColor: PLATISOL_DEFAULT_EXTERIOR_COLOR,
      productType: 'plastisol',
      meters: DEFAULT_PLATISOL_CONFIG.meters,
    });

  const { config, setConfig, navLoadToken, lastNavLoadedConfig } = useConfiguratorLoadState(
    location,
    buildDefaultConfig,
    (loaded) => {
      const legacyMeters =
        Number(loaded?.meters) > 0
          ? Number(loaded.meters)
          : (Number(loaded?.width) > 0 && Number(loaded?.height) > 0
              ? (Number(loaded.width) * Number(loaded.height)) / 1_000_000
              : DEFAULT_PLATISOL_CONFIG.meters);
      return withDefaultAdditionalItems({
        ...loaded,
        profileSystem: normalizePlastisolProfileSystem(loaded?.profileSystem),
        productType: 'plastisol',
        exteriorColor: loaded?.exteriorColor || PLATISOL_DEFAULT_EXTERIOR_COLOR,
        meters: legacyMeters,
      });
    }
  );
  const { isDirty, markClean, markDirty, resetBaseline } = useConfiguratorDirtyState(config);

  useEffect(() => {
    if (navLoadToken > 0 && lastNavLoadedConfig) {
      resetBaseline(lastNavLoadedConfig);
    }
  }, [navLoadToken, lastNavLoadedConfig, resetBaseline]);

  const handleReset = () => {
    const nextConfig = buildDefaultConfig();
    setConfig(nextConfig);
    resetBaseline(nextConfig);
  };

  const handleAdditionalItemsChange = (updater) => {
    markDirty();
    setConfig((prev) => applyAdditionalItemsChange(prev, updater));
  };

  const handleConfigChange = (field, value) => {
    markDirty();
    setConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <ConfiguratorShell
      title={t('configurator.plastisolConfigurator')}
      drawingBoardRef={drawingBoardRef}
      onReset={handleReset}
      isDirty={isDirty}
      onMarkClean={markClean}
      saveCurrentRef={saveCurrentRef}
      previewCentered
      optionsPanel={
        <div className="h-full overflow-y-auto p-4 bg-white border-r border-gray-200">
          <PlastisolOptionsPanel
            config={config}
            onConfigChange={handleConfigChange}
            additionalItems={config.additionalItems || createDefaultAdditionalItems()}
            onAdditionalItemsChange={handleAdditionalItemsChange}
          />
        </div>
      }
      previewPanel={
        <PlastisolDrawingBoard
          meters={config.meters}
          width={config.width}
          height={config.height}
          onMetersChange={(nextMeters) => {
            markDirty();
            setConfig((prev) => ({ ...prev, meters: nextMeters }));
          }}
        />
      }
      summaryPanel={
        <Summary
          config={config}
          pricing={PRICING}
          profileSystems={plastisolProfileSystems}
          glassTextures={glassTextures}
          glazingTypes={glazingTypes}
          colors={colors}
          raamtypes={[]}
          hardware={[]}
          accessories={[]}
          woodJointOptions={woodJointOptions}
          partsOptions={[]}
          overheadOptions={[]}
          drawingBoardRef={drawingBoardRef}
          pageType="plastisol"
          onConfigChange={handleConfigChange}
          saveCurrentRef={saveCurrentRef}
          onSaved={(savedConfig, options) => {
            if (options?.resetToDefault) {
              handleReset();
              return;
            }
            resetBaseline(savedConfig || config);
          }}
        />
      }
    />
  );
};

export default PlastisolZetwerk;
